<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
      <h2 class="mb-0">Relatório de Movimento Financeiro</h2>
      <v-btn outlined color="primary" @click="$router.push('/relatorios#financeiro')">
        <v-icon left>mdi-arrow-left</v-icon>
        Voltar ao menu
      </v-btn>
    </div>

    <div class="d-flex flex-wrap align-center mt-4 mb-4" style="gap: 12px;">
      <v-text-field v-model="filtros.dataInicio" type="date" label="Data inicial" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.dataFim" type="date" label="Data final" solo hide-details clearable class="c-filtro" />

      <v-autocomplete
        v-model="filtros.fornecedor"
        :items="fornecedorOptions"
        label="Fornecedor"
        clearable
        solo
        hide-details
        class="c-filtro"
      />

      <v-autocomplete
        v-model="filtros.banco"
        :items="bancoOptions"
        label="Banco"
        clearable
        solo
        hide-details
        class="c-filtro"
      />

      <v-btn color="primary" :loading="loading" @click="buscarDados">
        <v-icon left>mdi-refresh</v-icon>
        Recarregar
      </v-btn>

      <v-menu>
        <template v-slot:activator="{ on }">
          <v-btn color="primary" outlined v-on="on" :disabled="loading || dadosFiltrados.length === 0">
            <v-icon left>mdi-file-export-outline</v-icon>
            Exportar
          </v-btn>
        </template>
        <v-list>
          <v-list-item link @click="gerarExcel"><v-list-item-title>Excel</v-list-item-title></v-list-item>
          <v-list-item link @click="gerarPdf"><v-list-item-title>PDF</v-list-item-title></v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div ref="areaPdf">
      <v-data-table :headers="headers" :items="dadosFiltrados" :loading="loading" item-key="_id" rows-per-page-text="Linhas por página:" page-text="{0}-{1} de {2}" class="elevation-1">
        <template v-slot:item.data="{ item }">{{ formatarDataPtBr(item.data) }}</template>
        <template v-slot:item.valorMenos="{ item }">{{ formatarMoedaPtBr(item.valorMenos) }}</template>
        <template v-slot:item.valorMais="{ item }">{{ formatarMoedaPtBr(item.valorMais) }}</template>
        <template v-slot:item.saldo="{ item }">{{ formatarMoedaPtBr(item.saldoCalculado) }}</template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import relatorioMovimentoFinanceiroService from "@/views/Relatorios/services/relatorioMovimentoFinanceiroService";
import { exportarExcel, exportarPdfTabela, formatarDataPtBr, formatarMoedaPtBr, normalizarTexto } from "@/views/Relatorios/base/relatorioBaseUtils";

export default {
  name: "RelatorioMovimentoFinanceiroView",
  data() {
    return {
      loading: false,
      dados: [],
      filtros: { dataInicio: "", dataFim: "", fornecedor: "", banco: "" },
      headers: [
        { text: "Data", value: "data" },
        { text: "Informações", value: "informacoes" },
        { text: "Fornecedor", value: "fornecedor" },
        { text: "Valor -", value: "valorMenos" },
        { text: "Valor +", value: "valorMais" },
        { text: "Saldo", value: "saldo" },
        { text: "Banco", value: "banco" }
      ]
    };
  },
  computed: {
    fornecedorOptions() {
      return Array.from(new Set(this.dados.map((item) => normalizarTexto(item.fornecedor)).filter(Boolean))).sort();
    },
    bancoOptions() {
      return Array.from(new Set(this.dados.map((item) => normalizarTexto(item.banco)).filter(Boolean))).sort();
    },
    dadosFiltrados() {
      const inicio = this.filtros.dataInicio ? new Date(this.filtros.dataInicio) : null;
      const fim = this.filtros.dataFim ? new Date(this.filtros.dataFim) : null;
      if (fim) fim.setHours(23, 59, 59, 999);

      const filtrado = this.dados.filter((item) => {
        const dataLinha = item.data ? new Date(item.data) : null;
        const fornecedorOk = !this.filtros.fornecedor || normalizarTexto(item.fornecedor) === normalizarTexto(this.filtros.fornecedor);
        const bancoOk = !this.filtros.banco || normalizarTexto(item.banco) === normalizarTexto(this.filtros.banco);

        if (!dataLinha || Number.isNaN(dataLinha.getTime())) return fornecedorOk && bancoOk;

        const dataInicioOk = !inicio || dataLinha >= inicio;
        const dataFimOk = !fim || dataLinha <= fim;

        return fornecedorOk && bancoOk && dataInicioOk && dataFimOk;
      });

      const ordenado = [...filtrado].sort((a, b) => new Date(a.data || 0) - new Date(b.data || 0));
      let saldoAcumulado = 0;

      return ordenado.map((item) => {
        saldoAcumulado += Number(item.valorMais || 0) - Number(item.valorMenos || 0);
        const saldoCalculado = Number(item.saldo || 0) !== 0 ? Number(item.saldo || 0) : saldoAcumulado;
        return { ...item, saldoCalculado };
      });
    }
  },
  watch: {
    'filtros.dataInicio'() { this.buscarDados(); },
    'filtros.dataFim'() { this.buscarDados(); }
  },
  methods: {
    formatarDataPtBr,
    formatarMoedaPtBr,
    async buscarDados() {
      this.loading = true;
      try {
        const lista = await relatorioMovimentoFinanceiroService.listarMovimentoFinanceiro(this.filtros);
        this.dados = Array.isArray(lista) ? lista : [];
      } finally {
        this.loading = false;
      }
    },
    gerarExcel() {
      const linhas = this.dadosFiltrados.map((item) => ({
        Data: this.formatarDataPtBr(item.data),
        Informações: item.informacoes,
        Fornecedor: item.fornecedor,
        "Valor -": item.valorMenos,
        "Valor +": item.valorMais,
        Saldo: item.saldoCalculado,
        Banco: item.banco
      }));
      exportarExcel(linhas, "relatorio-movimento-financeiro.xlsx");
    },
    async gerarPdf() {
      await exportarPdfTabela({
        headers: this.headers,
        rows: this.dadosFiltrados,
        nomeArquivo: "relatorio-movimento-financeiro.pdf",
        tituloRelatorio: "Relatório de Movimento Financeiro",
        formatadoresPorCampo: {
          data: (valor) => this.formatarDataPtBr(valor),
          valorMenos: (valor) => this.formatarMoedaPtBr(valor),
          valorMais: (valor) => this.formatarMoedaPtBr(valor),
          saldo: (valor, row) => this.formatarMoedaPtBr(row?.saldoCalculado ?? valor)
        }
      });
    }
  },
  async mounted() {
    await this.buscarDados();
  }
};
</script>

<style scoped>
.c-filtro { min-width: 220px; max-width: 280px; }
</style>
